import { useState } from "react";
import { subscriptionService } from "@miplatform/api";
import { HTTP_STATUSES, MODULE_PERMISSION_MAP } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IApiResponse, IRoleResponse } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { IRoleFormData } from "../../views/Config/Roles/ConfigRoles.types";

const getRoleUUID = createUUID();
const createRoleUUID = createUUID();
const updateRoleUUID = createUUID();
const deleteRoleUUID = createUUID();
const duplicateRoleUUID = createUUID();

export const useRole = () => {
  const stateManager = useComponentState();
  const [roleDetails, setRoleDetails] = useState<IRoleResponse>();
  const [error, setError] = useState<Error | null>(null);

  const getRole = (id: string): Promise<IApiResponse<IRoleResponse>> => {
    stateManager.abort(getRoleUUID);
    return stateManager.run(async () => {
      const response = await subscriptionService.roles.get(id);
      if (response.status === HTTP_STATUSES.OK) {
        setRoleDetails(response.data);
      }
      return response;
    }, getRoleUUID);
  };

  const createRole = (
    data: IRoleFormData
  ): Promise<IApiResponse<IRoleResponse>> => {
    stateManager.abort(createRoleUUID);
    return stateManager.run(
      async () =>
        subscriptionService.roles.create({
          data: {
            ...data,
            permissionNames: [
              ...data.permissionNames,
              ...data.modules.map((m) => MODULE_PERMISSION_MAP[m].access),
            ],
          },
          toastMessageConfig: {
            success: { text: `Role ${data.name} created successfully` },
          },
        }),
      createRoleUUID
    );
  };

  const updateRole = (
    data: IRoleFormData
  ): Promise<IApiResponse<IRoleResponse>> => {
    stateManager.abort(updateRoleUUID);
    return stateManager.run(
      async () =>
        subscriptionService.roles.update({
          id: data.id,
          data: {
            ...data,
            permissionNames: [
              ...data.permissionNames,
              ...data.modules.map((m) => MODULE_PERMISSION_MAP[m].access),
            ],
          },
          toastMessageConfig: {
            success: { text: `Role ${data.name} updated successfully` },
          },
        }),
      updateRoleUUID
    );
  };

  const deleteRole = (
    id: string,
    modifiedOn: string
  ): Promise<IApiResponse> => {
    stateManager.abort(deleteRoleUUID);
    return stateManager.run(
      async () =>
        subscriptionService.roles.delete({
          id,
          data: { modifiedOn },
          toastMessageConfig: {
            success: { text: `Role deleted successfully` },
          },
        }),
      deleteRoleUUID
    );
  };

  const duplicateRole = (
    newName: string,
    roleId: string
  ): Promise<IApiResponse | null> => {
    stateManager.abort(duplicateRoleUUID);
    return stateManager.run(async () => {
      const existRoleResponse = await subscriptionService.roles.get(roleId);

      if (existRoleResponse.status !== HTTP_STATUSES.OK) {
        setError(new Error("Failed to fetch the existing role to duplicate."));
        return null;
      }

      const roleData = existRoleResponse.data as IRoleResponse;

      const response = await subscriptionService.roles.create({
        data: {
          name: newName,
          description: roleData.description,
          isSystemDefined: roleData.isSystemDefined,
          permissionNames: roleData.permissionNames,
          departmentId: roleData.departmentId,
        },
        toastMessageConfig: {
          success: { text: `Role ${newName} duplicated successfully` },
        },
      });

      if (response.status === HTTP_STATUSES.CONFLICT) {
        setError(new Error("A role with this name already exists."));
        return response;
      }

      return response;
    }, duplicateRoleUUID);
  };

  return {
    roleDetails,
    getRole,
    createRole,
    updateRole,
    deleteRole,
    duplicateRole,
    loading: stateManager.isBusy(),
    error,
  };
};
